import "./LoginPage.css";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useUserContext } from "../Context/UserContext";
import { sanitizeReturnPath } from "./authRouting";
import { formatPrice } from "../Reviews/reviewPrice";

interface LoginResponse {
  id: number;
  username: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  profile?: string;
  role?: string;
  credits: string;
  mfaRequired?: boolean;
  // password //deleted
  // email //deleted
  // isEmailVerified //deleted
}

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  // A pending account request is not an error; it is shown as information.
  const [errorIsPending, setErrorIsPending] = useState(false);
  const { user, setUser } = useUserContext();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("expired") === "true";
  const authenticationRequired =
    searchParams.get("reason") === "authentication-required";
  const returnUrl = sanitizeReturnPath(searchParams.get("returnUrl"), "/");
  const oauthStatus = searchParams.get("oauth");
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3201";

  const handleGoogleSignIn = () => {
    const returnTo = `${window.location.origin}${returnUrl}`;
    const params = new URLSearchParams({
      origin: "work",
      returnTo,
    });
    window.location.assign(`${backendUrl}/auth/google?${params.toString()}`);
  };

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // Whether Google OAuth is configured on the backend; null until /api/config
  // answers, while the button's space is reserved so the form does not jump.
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState<boolean | null>(
    null
  );
  const [beta, setBeta] = useState(false);
  const [reviewPrice, setReviewPrice] = useState<{
    priceCents: number;
    turnaroundHours: number;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${backendUrl}/api/config`);
        if (cancelled) return;
        setGoogleOAuthEnabled(!!res.data?.auth?.google_oauth_enabled);
        setBeta(Boolean(res.data?.beta?.enabled));
        const reviews = res.data?.reviews;
        if (reviews?.price_cents && reviews?.turnaround_hours)
          setReviewPrice({
            priceCents: reviews.price_cents,
            turnaroundHours: reviews.turnaround_hours,
          });
      } catch {
        if (!cancelled) setGoogleOAuthEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [backendUrl]);

  let navigate = useNavigate();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorIsPending(false);

    try {
      const response = await axios.post<LoginResponse>(
        `${backendUrl}/api/login`,
        { username, password },
        { withCredentials: true }
      );
      if (response.data.mfaRequired) {
        setMfaRequired(true);
        setMfaError("");
        setMfaCode("");
        setResendCooldown(60);
        return;
      }
      setUser(response.data);
      navigate(returnUrl, { state: { user: response.data } });
    } catch (error: any) {
      if (
        error?.response?.data?.message &&
        [401, 403].includes(error?.response?.status)
      ) {
        setError(error.response.data.message);
        setErrorIsPending(error.response.data.code === "pending");
      } else if (
        error?.response?.data === "Unauthorized" &&
        error?.response?.status === 401
      ) {
        setError("Incorrect email or password.");
      } else {
        setError("Sign-in failed. Please try again.");
      }
      console.error(error);
    }
  };

  const handleMfaVerify = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMfaError("");
    try {
      const response = await axios.post(
        `${backendUrl}/api/verify-mfa`,
        { code: mfaCode },
        { withCredentials: true }
      );
      setUser(response.data);
      navigate(returnUrl, { state: { user: response.data } });
    } catch (error: any) {
      let msg = error?.response?.data?.message || "Verification failed.";
      const remaining = error?.response?.data?.attemptsRemaining;
      if (remaining !== undefined) {
        msg += ` ${remaining} attempt(s) remaining.`;
      }
      setMfaError(msg);
      if (msg.includes("log in again")) {
        setMfaRequired(false);
        setMfaCode("");
        setError(msg);
      }
    }
  };

  const handleResendCode = useCallback(async () => {
    try {
      await axios.post(
        `${backendUrl}/api/resend-mfa`,
        {},
        { withCredentials: true }
      );
      setMfaError("");
      setMfaCode("");
      setResendCooldown(60);
    } catch (error: any) {
      setMfaError(error?.response?.data?.message || "Failed to resend code.");
    }
  }, [backendUrl]);

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaCode("");
    setMfaError("");
    setError("");
  };

  if (user && !mfaRequired) return <Navigate replace to={returnUrl} />;
  return (
    <main className="login-container">
      <div className="login-content">
        <aside className="login-introduction">
          <span className="login-intro-eyebrow">
            AI ANSWERS. ATTORNEY JUDGMENT.
          </span>
          <h2>
            If it matters,
            <br />
            get it reviewed.
          </h2>
          <ol className="login-intro-steps">
            <li>
              {beta
                ? "Ask a question using a generic example."
                : "Ask your legal question."}
            </li>
            <li>Get an AI answer, free.</li>
            <li>
              {beta
                ? "Try the attorney review workflow with a simulated $150 fee. No charge is made."
                : "When it matters, an attorney checks it and answers in writing"}
              {!beta && reviewPrice
                ? ` — ${formatPrice(reviewPrice.priceCents)}, within ${
                    reviewPrice.turnaroundHours
                  } hours after the agreed review starts. Matching comes first.`
                : !beta
                  ? "."
                  : ""}
            </li>
          </ol>
          <div className="login-intro-note">
            {beta
              ? "Test beta: use generic examples. No charges or legal services are provided."
              : "Attorney reviews are provided by licensed attorneys."}
          </div>
        </aside>
        <div className="login-card">
          <h1>{mfaRequired ? "Verify your sign-in" : "Sign in to Reviewed"}</h1>
          <p className="text-secondary">
            {mfaRequired
              ? "Enter the code sent to your email."
              : "Welcome back. Continue where you left off."}
          </p>
          {(sessionExpired || authenticationRequired) && (
            <div className="alert alert-warning">
              Please sign in to continue.
            </div>
          )}
          {(error || mfaError) && (
            <div
              role="alert"
              className={`alert ${
                errorIsPending && !mfaRequired ? "alert-info" : "alert-danger"
              }`}
            >
              {mfaRequired ? mfaError : error}
            </div>
          )}
          {oauthStatus && oauthStatus !== "success" && (
            <div role="alert" className="alert alert-danger">
              Google sign-in failed. Please try again.
            </div>
          )}
          {mfaRequired ? (
            <>
              <form onSubmit={handleMfaVerify}>
                <label htmlFor="mfaCode">Verification code</label>
                <input
                  className="form-control mb-3"
                  id="mfaCode"
                  value={mfaCode}
                  onChange={e =>
                    setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  required
                />
                <button className="submit-button-signup" type="submit">
                  Verify
                </button>
              </form>
              <button
                className="btn btn-link"
                disabled={resendCooldown > 0}
                onClick={handleResendCode}
              >
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend code"}
              </button>
              <button className="btn btn-link" onClick={handleBackToLogin}>
                Back to sign in
              </button>
            </>
          ) : (
            <>
              <form onSubmit={handleLogin}>
                <label htmlFor="username">Email</label>
                <input
                  className="form-control mb-3"
                  id="username"
                  autoComplete="username"
                  inputMode="email"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                />
                <label htmlFor="password">Password</label>
                <input
                  className="form-control mb-3"
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
                <button className="submit-button-signup mb-3" type="submit">
                  Sign in
                </button>
              </form>
              {googleOAuthEnabled === null && (
                <div className="login-google-slot mb-3" aria-hidden="true" />
              )}
              {googleOAuthEnabled && (
                <button
                  className="btn btn-outline-secondary w-100 mb-3"
                  onClick={handleGoogleSignIn}
                >
                  Sign in with Google
                </button>
              )}
              <Link
                className="login-request-access"
                to={`/signup?returnUrl=${encodeURIComponent(returnUrl)}`}
              >
                New to Reviewed? Request access
              </Link>
              <p className="mt-4 mb-0">
                <Link to="/work-with-us">
                  For attorneys: apply to review questions →
                </Link>
              </p>
              <div className="login-links">
                <Link to="/requestpasswordreset">Reset password</Link>
                {user && <Link to="/documents">Go to documents</Link>}
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};
export default LoginPage;
